import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import WaText from './waFormat';
import { Icon, ChannelBadge, channelLabel } from './inboxIcons';
import { themeStyle, DEFAULT_ACCENT } from './inboxTheme';
import ProblemReport, { reportProblem, FlagButton } from './ProblemReport';
import InboxDraft, { STATUS_AFTER, AI_PICK } from './InboxDraft';
import InboxCustomer from './InboxCustomer';
import InboxAttachment from './InboxAttachment';
import { useReplyFiles, FileChips, PendingFiles, channelProblem, ACCEPT } from './InboxFiles';
import InboxWholesale from './InboxWholesale';
import InboxCart, { groupCarts, cartStatus, isDue, cartName, inr, cartDueMs } from './InboxCart';
import './inbox.css';

const STATUS_LABEL = { new: 'New', open: 'In progress', waiting: 'Waiting on customer', resolved: 'Resolved' };
// Shown on rows only (not something you set by hand): the customer only tapped buttons since our last reply
const CHIP_LABEL = { ...STATUS_LABEL, self_service: 'Self-service' };
const NOT_OPEN = new Set(['resolved', 'self_service']);
// Display status → stored ticket status
const TO_STORED = { new: 'new', open: 'open', waiting: 'pending', resolved: 'resolved' };
const DM = new Set(['instagram', 'messenger', 'whatsapp']);
// Background tabs don't refresh; coming back refreshes at once (saves database reads)
// The list can stay cached for a while now, so a snooze ends by the clock, not by the next reload
const isSnoozed = (i) => !!i.snoozedUntilMs && i.snoozedUntilMs > Date.now();
// Rows that open something other than a conversation: carts and wholesale registrations
const notThread = (id) => /^(cart|ws):/.test(String(id || ''));
const tabHidden = () => typeof document !== 'undefined' && document.hidden;
const SRC_CH = { email: 'email', contact_form: 'form', instagram: 'ig', messenger: 'fb', whatsapp: 'wa', chatbot: 'chat', chatbot_wholesale: 'chat', manual: 'manual' };

const TEMPLATES = [
  'Fabrics are printed fresh for each order, so dispatch takes 2 to 4 working days. Delivery is usually 5 to 7 days after dispatch.',
  'Could you share your order number and the phone number used for the order? I will check it right away.',
  'Please share an unboxing video, or clear photos if you do not have one, within 3 days of delivery. Keep the fabric unused and uncut for now.',
  'You can register for wholesale here: fabvoguestudio.com/pages/wholesale. Approval takes 24 to 48 hours.',
];

const tsMs = (t) => (!t ? 0 : typeof t === 'number' ? t : t._seconds ? t._seconds * 1000 : Date.parse(t) || 0);
const initials = (name) => String(name || '?').replace(/^@/, '').split(/[\s@.]+/).filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

function shortTime(msVal) {
  if (!msVal) return '';
  const d = new Date(msVal);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((now - d) / 86400e3);
  if (days < 7) return d.toLocaleDateString('en-IN', { weekday: 'short' });
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function dayLabel(msVal) {
  const d = new Date(msVal);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === new Date(now - 86400e3).toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
}

// Split a customer's email into what they just wrote and the quoted history below it
const QUOTE_SELECTORS = '.gmail_quote, .gmail_extra, blockquote, .yahoo_quoted, #appendonsend, [id^="divRplyFwdMsg"], .moz-cite-prefix, #mail-editor-reference-message-container, .OutlookMessageHeader';
const TEXT_QUOTE = /^(On .{4,200} wrote:|-{2,}\s*Original Message\s*-{2,}|From: .+|>.*|_{8,})$/im;

function splitHtml(html) {
  const clean = DOMPurify.sanitize(html, { FORBID_TAGS: ['style', 'script'], FORBID_ATTR: ['style'] });
  const doc = new DOMParser().parseFromString(`<div id="cx-root">${clean}</div>`, 'text/html');
  const root = doc.getElementById('cx-root');
  const quote = root.querySelector(QUOTE_SELECTORS);
  if (!quote) return { main: clean, quoted: null };
  // Drop the quote and everything after it; the "On ... wrote:" line usually sits just before
  let n = quote;
  while (n && n !== root) {
    while (n.nextSibling) n.nextSibling.remove();
    n = n.parentNode;
  }
  quote.remove();
  const main = root.innerHTML.replace(/(<br\s*\/?>\s*)*(<div[^>]*>\s*)?On [^<]{4,200}wrote:\s*(<\/div>)?\s*(<br\s*\/?>\s*)*$/i, '');
  return root.textContent.trim() ? { main, quoted: clean } : { main: clean, quoted: null };
}

function splitText(text) {
  const m = TEXT_QUOTE.exec(String(text || ''));
  if (!m || m.index < 2) return { main: text, quoted: null };
  return { main: text.slice(0, m.index).trimEnd(), quoted: text };
}

// Who we're talking to, as the channel knows them: WhatsApp number, Instagram @handle or email
const fmtPhone = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  if (d.length === 10) return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
  return d ? `+${d}` : '';
};
function contactOf(item, ticket) {
  const ch = item?.channel;
  if (ch === 'wa') return fmtPhone(item?.handle || ticket?.customerPhone) || 'WhatsApp';
  if (ch === 'ig') return item?.handle ? `@${item.handle}` : (ticket?.metaUsername ? `@${ticket.metaUsername}` : 'Instagram');
  if (ch === 'fb') return 'Messenger';
  return item?.email || ticket?.customerEmail || channelLabel(ch);
}

// What the team should do for each WhatsApp task
const TASK_HINT = {
  cod_call: 'No reply to the COD confirmation. Call before printing.',
  return_request: 'Customer asked for a replacement from WhatsApp. Check their photos or video, then create the return in Order Desk.',
  talk_to_team: 'Customer tapped "Talk to us" from their order status. Reply here.',
  late_order: 'Customer checked a late order and was promised an update. Check the order, then reply with a new date.',
  change_address: 'Update the address in Shopify before printing.',
  cancel_request: 'Customer asked to cancel. Review and confirm in Shopify.',
  unhappy: 'Customer is not happy after delivery. Ask what went wrong.',
  ndr_reattempt: 'Courier could not deliver. Request the reattempt in the Delhivery panel.',
  ndr_address: 'Courier could not deliver. Update the address with Delhivery once the customer sends it.',
  ndr_call: 'Courier could not deliver. Call the customer to arrange delivery.',
};

// WhatsApp delivery ticks on our own messages
const WA_STATUS = { sent: 'Sent', delivered: 'Delivered', read: 'Read' };
const clockTime = (v) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '');

// WhatsApp-style ticks on our messages: clock = sending, one tick = sent, two = delivered, two blue = read
function WaTicks({ status, at = {} }) {
  const title = ['sent', 'delivered', 'read'].filter((k) => at[k]).map((k) => `${WA_STATUS[k]} ${clockTime(at[k])}`).join(' · ') || 'Sending';
  if (!status) {
    return <span className="cx-wa-ticks" title={title} aria-label="Sending"><svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3l2 1.5" strokeLinecap="round" /></svg></span>;
  }
  const two = status === 'delivered' || status === 'read';
  return (
    <span className={`cx-wa-ticks${status === 'read' ? ' read' : ''}`} title={title} aria-label={WA_STATUS[status]}>
      <svg viewBox="0 0 18 12" width="17" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {two ? <><path d="M1 6.5l3.2 3.2L11 2.5" /><path d="M8.2 9.7L15.4 2.5" /></> : <path d="M3 6.5l3.2 3.2L13 2.5" />}
      </svg>
    </span>
  );
}

function WindowChip({ expiresMs, strict }) {
  if (!expiresMs) return null;
  const left = expiresMs - Date.now();
  if (left <= 0 && strict) {
    return <span className="cx-chip cx-c-time hot" title="WhatsApp allows free-form replies only within 24 hours of the customer's last message. After that only an approved template can be sent.">Window closed · template only</span>;
  }
  if (left <= 0) {
    const humanLeft = expiresMs + 6 * 86400e3 - Date.now();
    return <span className="cx-chip cx-c-time hot" title="After 24 hours Meta only allows human-agent replies, for up to 7 days">{humanLeft > 0 ? `${Math.ceil(humanLeft / 86400e3)}d left (human reply)` : 'Reply window closed'}</span>;
  }
  const h = Math.floor(left / 3600e3);
  const m = Math.floor((left % 3600e3) / 60000);
  return <span className={`cx-chip cx-c-time${left < 3 * 3600e3 ? ' hot' : ''}`} title={strict ? 'WhatsApp allows replies for 24 hours after the customer\'s last message' : 'Meta allows normal replies for 24 hours after the customer\'s last message'}><Icon name="clock" />{h ? `${h}h ` : ''}{m}m left</span>;
}

// Snooze presets: label, and a function giving the wake-up time
function snoozeOptions(now = new Date()) {
  const at = (d, h) => { const x = new Date(d); x.setHours(h, 0, 0, 0); return x; };
  const tomorrow = at(new Date(now.getTime() + 86400e3), 10);
  const monday = at(new Date(now.getTime() + (((8 - now.getDay()) % 7) || 7) * 86400e3), 10);
  const opts = [
    ['In 3 hours', new Date(now.getTime() + 3 * 3600e3)],
    ['Tomorrow morning', tomorrow],
    ['Next Monday', monday],
    ['In a week', at(new Date(now.getTime() + 7 * 86400e3), 10)],
  ];
  if (now.getHours() < 15) opts.unshift(['This evening', at(now, 18)]);
  return opts;
}

const wakeLabel = (msVal) => {
  const d = new Date(msVal);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const firstName = (n) => String(n || 'Teammate').split(/[\s@]/)[0];

function Inbox({ apiBase, getToken, me, perms, selectRequest, onSelectHandled, accent: accentProp, fill, refreshKey, onNewTicket, onArchive, onUnreadChange }) {
  const { activeFirebaseApp } = useAuth();
  const rootRef = useRef(null);
  const [height, setHeight] = useState(640);
  const [items, setItems] = useState(null);
  const [carts, setCarts] = useState(null);
  const [wholesale, setWholesale] = useState(null); // registrations waiting for approval (every Support user)
  const [members, setMembers] = useState([]);
  const [accentState, setAccent] = useState(DEFAULT_ACCENT);
  const accent = accentProp || accentState;
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeCustom, setSnoozeCustom] = useState('');
  const [viewers, setViewers] = useState([]);
  const [showQuoted, setShowQuoted] = useState({});
  const [hasDraft, setHasDraft] = useState({}); // ticket id → a draft is waiting
  const [ownReply, setOwnReply] = useState({}); // ticket id → the reply box was opened by hand
  const [atts, setAtts] = useState({}); // ticket id → email attachments from Gmail
  // Combined timeline: every channel of the customer in one stream (remembered on this device)
  const [timelineOn, setTimelineOn] = useState(() => { try { return localStorage.getItem('cx-timeline') === '1'; } catch { return false; } });
  const [timeline, setTimeline] = useState(null); // { forId, data }
  const setTimelineMode = (on) => {
    setTimelineOn(on);
    try { localStorage.setItem('cx-timeline', on ? '1' : '0'); } catch { /* storage unavailable */ }
  };
  // Layout preferences, remembered on this device
  const [viewsOff, setViewsOff] = useState(() => { try { return localStorage.getItem('cx-views-off') === '1'; } catch { return false; } });
  const [listW, setListW] = useState(() => { try { return Number(localStorage.getItem('cx-list-w')) || null; } catch { return null; } });
  const toggleViews = () => setViewsOff((v) => {
    try { localStorage.setItem('cx-views-off', v ? '0' : '1'); } catch { /* storage unavailable */ }
    return !v;
  });
  const startResize = (e) => {
    const listEl = rootRef.current?.querySelector('.cx-list');
    if (!listEl) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = listEl.getBoundingClientRect().width;
    let w = startW;
    const move = (ev) => {
      if (!Number.isFinite(ev.clientX) || !Number.isFinite(startX)) return;
      w = Math.round(Math.min(640, Math.max(260, startW + ev.clientX - startX)));
      setListW(w);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      if (w !== startW) { try { localStorage.setItem('cx-list-w', String(w)); } catch { /* storage unavailable */ } }
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const resetWidth = () => { setListW(null); try { localStorage.removeItem('cx-list-w'); } catch { /* storage unavailable */ } };
  const [docked, setDocked] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1401px)').matches);
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1401px)');
    if (!mq) return undefined;
    const on = () => setDocked(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  const [agentOn, setAgentOn] = useState(false);
  const [view, setView] = useState('all');
  const [tab, setTab] = useState('open');
  const [cartTab, setCartTab] = useState('due');
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState('');
  const [mThread, setMThread] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [threadErr, setThreadErr] = useState(null);
  const [compose, setCompose] = useState({});
  const [mode, setMode] = useState({});
  const [editing, setEditing] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [draftKey, setDraftKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifyStatus, setNotifyStatus] = useState(false);
  const [keysOpen, setKeysOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const replyFiles = useReplyFiles({ apiBase, getToken });
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const toastTimer = useRef(null);

  const toast = useCallback((msg, isErr = false) => {
    setToastMsg({ msg, isErr });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 2800);
  }, []);

  const api = useCallback(async (path, method = 'GET', body) => {
    const token = await getToken();
    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
    return data;
  }, [apiBase, getToken]);

  // Raw file download with the signed-in user's token (links can't carry it)
  const fetchBlob = useCallback(async (path) => {
    const token = await getToken();
    const res = await fetch(`${apiBase}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return res.blob();
  }, [apiBase, getToken]);

  // Fill the screen below whatever sits above the inbox (the workspace does this when it hosts us)
  useEffect(() => {
    if (fill) return undefined;
    const fit = () => {
      const top = rootRef.current?.getBoundingClientRect().top || 0;
      setHeight(Math.max(560, window.innerHeight - top - 16));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fill]);

  // The server answers "unchanged" (plus who is viewing what) when nothing changed since our version
  const inboxVersion = useRef(null);
  const loadItems = useCallback(async () => {
    if (!perms.tickets && !perms.chats) { setItems([]); return; }
    try {
      const data = await api(`/api/v1/support/inbox${inboxVersion.current ? `?v=${encodeURIComponent(inboxVersion.current)}` : ''}`);
      if (data.unchanged) {
        const v = data.viewers || {};
        setItems((prev) => {
          if (!prev) return prev;
          let changed = false;
          const next = prev.map((i) => {
            const now = v[i.id] || [];
            if (JSON.stringify(now) === JSON.stringify(i.viewers || [])) return i;
            changed = true;
            return { ...i, viewers: now };
          });
          return changed ? next : prev;
        });
        return;
      }
      inboxVersion.current = data.version || null;
      setItems(data.items || []);
    } catch (err) {
      inboxVersion.current = null;
      setItems((prev) => prev || []);
      toast(err.message, true);
    }
  }, [api, perms.tickets, perms.chats, toast]);

  const loadCarts = useCallback(async () => {
    if (!perms.carts) return;
    try {
      const data = await api('/api/v1/support/abandoned-checkouts?status=all&limit=200');
      setCarts(groupCarts(data.checkouts || []));
    } catch {
      setCarts((prev) => prev || []);
    }
  }, [api, perms.carts]);
  // Carts sync from Shopify every 15 min (9 am–6 pm); this pulls them now (replaces the classic screen's button)
  const [cartSync, setCartSync] = useState(false);
  const syncCarts = async () => {
    setCartSync(true);
    try { const r = await api('/api/v1/shopify/sync-checkouts', 'POST'); toast(r.message || 'Carts synced from Shopify'); await loadCarts(); } catch (err) { toast(err.message, true); } finally { setCartSync(false); }
  };

  const loadWholesale = useCallback(async () => {
    try { const data = await api('/api/v1/support/wholesale'); setWholesale(data.items || []); } catch { setWholesale((prev) => prev || []); }
  }, [api]);

  useEffect(() => {
    loadItems();
    loadCarts();
    loadWholesale();
    // Fallback only — live changes arrive through the pulse listener below
    let n = 0;
    const t = setInterval(() => { if (!tabHidden()) { loadItems(); loadCarts(); n += 1; if (n % 4 === 0) loadWholesale(); } }, 90000);
    const onVisible = () => { if (!tabHidden()) loadItems(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [loadItems, loadCarts, loadWholesale]);

  useEffect(() => {
    if (refreshKey) loadItems();
  }, [refreshKey, loadItems]);

  // Unread count for the rail badge
  useEffect(() => {
    if (items && onUnreadChange) onUnreadChange(items.filter((i) => i.status === 'new' && !isSnoozed(i)).length);
  }, [items, onUnreadChange]);

  useEffect(() => {
    if (!accentProp) api('/api/v1/support/appearance').then((d) => setAccent(d.accent || DEFAULT_ACCENT)).catch(() => {});
    api('/api/v1/support/team-members').then((d) => setMembers(d.members || [])).catch(() => {});
    api('/api/v1/support/agent/config').then((d) => setAgentOn(!!d.config?.enabled)).catch(() => {});
    const onAccent = (e) => setAccent(e.detail || DEFAULT_ACCENT);
    window.addEventListener('cx-accent', onAccent);
    return () => window.removeEventListener('cx-accent', onAccent);
  }, [api, accentProp]);

  // Opened from elsewhere (AI agent page, notifications)
  useEffect(() => {
    if (!selectRequest) return;
    setView('all');
    setSel(selectRequest);
    setMThread(true);
    onSelectHandled?.();
  }, [selectRequest, onSelectHandled]);

  const isCartView = view === 'carts';
  const views = useMemo(() => {
    // Snoozed conversations stay out of every view except Snoozed until they wake
    const open = (f) => (i) => !NOT_OPEN.has(i.status) && !isSnoozed(i) && f(i);
    const groups = [];
    if (perms.tickets || perms.chats) {
      groups.push({ group: 'Inbox', items: [
        { id: 'all', label: 'All open', icon: 'inbox', f: open(() => true) },
        { id: 'mine', label: 'Assigned to me', icon: 'user', f: open((i) => i.assignee?.uid === me) },
        { id: 'unassigned', label: 'Unassigned', icon: 'users', f: open((i) => !i.assignee) },
        { id: 'drafts', label: 'AI drafts ready', icon: 'spark', f: open((i) => i.draftStatus === 'pending') },
        { id: 'urgent', label: 'Urgent', icon: 'alert', f: open((i) => i.urgent) },
        { id: 'cases', label: 'Open cases', icon: 'case', f: open((i) => !!i.caseRef) },
        { id: 'snoozed', label: 'Snoozed', icon: 'snooze', f: (i) => isSnoozed(i) },
      ] });
      const ch = [];
      if (perms.tickets) ch.push({ id: 'ch-email', label: 'Email', ch: 'email', f: open((i) => i.channel === 'email') });
      if (perms.chats) {
        ch.push({ id: 'ch-ig', label: 'Instagram', ch: 'ig', f: open((i) => i.channel === 'ig') });
        ch.push({ id: 'ch-fb', label: 'Messenger', ch: 'fb', f: open((i) => i.channel === 'fb') });
        ch.push({ id: 'ch-wa', label: 'WhatsApp', ch: 'wa', f: open((i) => i.channel === 'wa') });
      }
      if (perms.tickets) ch.push({ id: 'ch-form', label: 'Forms and chat', ch: 'form', f: open((i) => ['form', 'chat', 'manual'].includes(i.channel)) });
      groups.push({ group: 'Channels', items: ch });
    }
    if (perms.carts) groups.push({ group: 'Sales', items: [{ id: 'carts', label: 'Recoverable carts', icon: 'cart', cart: true }] });
    if (perms.tickets || perms.chats) {
      groups.push({ group: 'Teams', items: [
        { id: 'team-support', label: 'Support', icon: 'users', f: open((i) => i.team === 'Support') },
        { id: 'team-wholesale', label: 'Wholesale', icon: 'case', f: open((i) => i.team === 'Wholesale') },
      ] });
    }
    return groups;
  }, [perms, me]);
  const allViews = useMemo(() => views.flatMap((g) => g.items), [views]);
  const current = allViews.find((v) => v.id === view) || allViews[0];

  useEffect(() => {
    if (current && current.id !== view) setView(current.id);
  }, [current, view]);

  const [minuteTick, setMinuteTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setMinuteTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  // Conversation list for the current view and tab
  const { tabs, rows } = useMemo(() => {
    const list = items || [];
    const term = q.trim().toLowerCase();
    const inView = list.filter((i) => (current?.f ? current.f({ ...i, status: NOT_OPEN.has(i.status) ? 'new' : i.status }) : false))
      .filter((i) => current?.id === 'snoozed' || !isSnoozed(i))
      .filter((i) => !term || `${i.name} ${i.email || ''} ${i.handle || ''} ${i.preview} ${i.caseRef || ''} ${i.subject || ''} ${i.orderNumber || ''}`.toLowerCase().includes(term));
    const t = {
      open: inView.filter((i) => i.status === 'new' || i.status === 'open'),
      waiting: inView.filter((i) => i.status === 'waiting'),
      self: inView.filter((i) => i.status === 'self_service'),
      resolved: inView.filter((i) => i.status === 'resolved'),
    };
    // Snoozed: one list, soonest to wake first
    if (current?.id === 'snoozed') return { tabs: t, rows: [...inView].sort((a, b) => a.snoozedUntilMs - b.snoozedUntilMs) };
    const sorted = [...t[tab]].sort((a, b) => (Number(b.urgent) - Number(a.urgent)) || (b.lastActivityMs - a.lastActivityMs));
    return { tabs: t, rows: sorted };
  }, [items, current, q, tab, minuteTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const cartRows = useMemo(() => {
    const list = carts || [];
    const term = q.trim().toLowerCase();
    const filtered = list.filter((c) => !term || `${cartName(c)} ${c.customer?.email || ''} ${c.customer?.phone || ''}`.toLowerCase().includes(term));
    const t = {
      due: filtered.filter((c) => isDue(c)),
      open: filtered.filter((c) => !['converted', 'ignored'].includes(c.recoveryStatus)),
      converted: filtered.filter((c) => c.recoveryStatus === 'converted'),
      ignored: filtered.filter((c) => c.recoveryStatus === 'ignored'),
    };
    return { tabs: t, rows: [...t[cartTab]].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) };
  }, [carts, q, cartTab]);

  // Recoverable carts in the main list, so none are forgotten: only carts that are due, only for people
  // who handle carts. If the customer already has an open conversation, the cart shows as a chip on it.
  const { dueCarts, cartByItem } = useMemo(() => {
    if (!perms.carts || !carts || !items) return { dueCarts: [], cartByItem: {} };
    const byEmail = new Map();
    const byPhone = new Map();
    items.filter((i) => i.status !== 'resolved').forEach((i) => {
      if (i.email) byEmail.set(i.email.trim().toLowerCase(), i.id);
      if (i.phone) byPhone.set(i.phone, i.id);
    });
    const chips = {};
    const due = [];
    carts.filter((c) => !['converted', 'ignored'].includes(c.recoveryStatus)).forEach((c) => {
      const e = String(c.customer?.email || '').trim().toLowerCase();
      const ph = String(c.customer?.phone || '').replace(/\D/g, '').slice(-10);
      const hit = (e && byEmail.get(e)) || (ph.length === 10 && byPhone.get(ph)) || null;
      if (hit) { if (!chips[hit]) chips[hit] = c; } else if (isDue(c)) due.push(c);
    });
    return { dueCarts: due, cartByItem: chips };
  }, [perms.carts, carts, items]);
  const cartsInList = !isCartView && current?.id === 'all' && tab === 'open';
  // Due carts sit in the Open list as ONE folded row (placed by its newest due cart, like a chat). Opening it lists
  // every due cart under it (high value first, then oldest due); opening a customer chat folds it again.
  const [cartsOpen, setCartsOpen] = useState(false);
  const seenDue = useRef(null); // due cart ids already seen, for the "new" badge on the folded row
  const cartGroup = useMemo(() => {
    if (!cartsInList || !dueCarts.length) return null;
    const term = q.trim().toLowerCase();
    const list = dueCarts
      .filter((c) => !term || `${cartName(c)} ${c.customer?.email || ''} ${c.customer?.phone || ''}`.toLowerCase().includes(term))
      .sort((a, b) => (Number((b.cart?.totalPrice || 0) >= 3000) - Number((a.cart?.totalPrice || 0) >= 3000)) || (cartDueMs(a) - cartDueMs(b)));
    if (!list.length) return null;
    if (seenDue.current === null) seenDue.current = new Set(dueCarts.map((c) => c.id));
    return {
      list,
      open: cartsOpen || !!term, // a search that matches a cart opens the group
      total: list.reduce((t, c) => t + (Number(c.cart?.totalPrice) || 0), 0),
      high: list.filter((c) => (c.cart?.totalPrice || 0) >= 3000).length,
      oldestMs: Math.min(...list.map((c) => cartDueMs(c))),
      newestMs: Math.max(...list.map((c) => cartDueMs(c))),
      fresh: list.filter((c) => !seenDue.current.has(c.id)).length,
    };
  }, [cartsInList, dueCarts, q, cartsOpen]);
  // Wholesale registrations waiting: one folded row too, at the top when one is past the promised 48 hours
  const [wsOpen, setWsOpen] = useState(false);
  const wsGroup = useMemo(() => {
    if (!cartsInList || !wholesale?.length) return null;
    const term = q.trim().toLowerCase();
    const list = wholesale.filter((w) => !term || `${w.company} ${w.name} ${w.email || ''} ${w.city || ''}`.toLowerCase().includes(term));
    if (!list.length) return null;
    return {
      list,
      open: wsOpen || !!term,
      overdue: list.filter((w) => w.ageHours > 48).length,
      oldestH: Math.max(...list.map((w) => w.ageHours || 0)),
      newestMs: Math.max(...list.map((w) => Date.parse(w.createdAt) || 0)),
    };
  }, [cartsInList, wholesale, q, wsOpen]);
  const listRows = useMemo(() => {
    if (!cartGroup && !wsGroup) return rows;
    const cartHead = cartGroup ? { id: 'cart-group', isGroup: true, urgent: false, lastActivityMs: cartGroup.newestMs } : null;
    const wsHead = wsGroup ? { id: 'ws-group', isGroup: true, urgent: wsGroup.overdue > 0, lastActivityMs: wsGroup.newestMs } : null;
    let out = [...rows, cartHead, wsHead].filter(Boolean).sort((a, b) => (Number(b.urgent) - Number(a.urgent)) || (b.lastActivityMs - a.lastActivityMs));
    const expand = (head, children) => { const at = out.indexOf(head) + 1; out = [...out.slice(0, at), ...children, ...out.slice(at)]; };
    if (cartGroup?.open) expand(cartHead, cartGroup.list.map((c) => ({ id: `cart:${c.id}`, isCart: true, cart: c })));
    if (wsGroup?.open) expand(wsHead, wsGroup.list.map((w) => ({ id: `ws:${w.key}`, isWs: true, ws: w })));
    return out;
  }, [cartGroup, wsGroup, rows]);
  const toggleCarts = () => {
    setCartsOpen((v) => !v);
    seenDue.current = new Set(dueCarts.map((c) => c.id));
  };
  // Opening a customer chat (click or J/K) folds the carts again; automatic selection doesn't
  const foldCartsFor = (id) => {
    if (id && !String(id).startsWith('cart:')) setCartsOpen(false);
    if (id && !String(id).startsWith('ws:')) setWsOpen(false);
  };

  // "Not a bot": a real customer was marked as an automated sender (automatedSenders.js)
  const notBot = async () => {
    try { await api(`/api/v1/support/tickets/${sel}/not-bot`, 'POST'); toast('Marked as a real customer. Automatic notes and drafts are back on.'); loadItems(); } catch (err) { toast(err.message, true); }
  };

  // Keep a sensible selection
  useEffect(() => {
    if (isCartView) {
      if (!cartRows.rows.find((c) => `cart:${c.id}` === sel) && !mThread) setSel(cartRows.rows[0] ? `cart:${cartRows.rows[0].id}` : null);
    } else if (items && !listRows.find((i) => i.id === sel) && !mThread && !(items.find((i) => i.id === sel))) {
      setSel(listRows.find((i) => !i.isGroup)?.id || null);
    }
  }, [isCartView, cartRows, listRows, sel, mThread, items]);

  const selItem = !isCartView && items ? items.find((i) => i.id === sel) : null;
  const selRef = useRef(sel);
  selRef.current = sel;
  const selCart = carts && sel?.startsWith('cart:') ? carts.find((c) => `cart:${c.id}` === sel) || null : null;
  const selWs = wholesale && sel?.startsWith('ws:') ? wholesale.find((w) => `ws:${w.key}` === sel) || null : null;

  // Opened conversations are kept in memory: reopening one shows it at once, then refreshes quietly
  const threadCache = useRef(new Map());
  const cacheThread = (t) => {
    const c = threadCache.current;
    c.delete(t.id);
    c.set(t.id, t);
    if (c.size > 40) c.delete(c.keys().next().value);
  };
  const fetchThread = useCallback(async (id) => {
    const data = await api(`/api/v1/support/tickets/${id}`);
    const t = { id, ticket: data.ticket, comms: data.communications || [] };
    cacheThread(t);
    return t;
  }, [api]);
  const loadThread = useCallback(async (id, quiet) => {
    if (!id || notThread(id)) { setThread(null); return; }
    const cached = threadCache.current.get(id);
    if (!quiet) { setThread(cached || null); setThreadErr(null); }
    try {
      const t = await fetchThread(id);
      // a slow answer for a conversation the user already left must not replace the one on screen
      if (selRef.current === id) setThread((cur) => (cur && cur.id === id && (cur.comms || []).some((c) => c.pending) ? { ...t, comms: [...t.comms, ...cur.comms.filter((c) => c.pending)] } : t));
    } catch (err) {
      if (!quiet && !cached && selRef.current === id) setThreadErr(err.message);
    }
  }, [fetchThread]);
  // Warm the cache with the conversations next to the open one, so moving down the list is instant
  const prefetch = useCallback((id) => {
    if (!id || notThread(id) || threadCache.current.has(id)) return;
    fetchThread(id).catch(() => {});
  }, [fetchThread]);

  useEffect(() => {
    setMenuOpen(false);
    setSnoozeOpen(false);
    setShowTemplates(false);
    setSendErr(null);
    loadThread(sel);
    if (!sel || notThread(sel)) return undefined;
    const t = setInterval(() => { if (!tabHidden()) loadThread(sel, true); }, 90000);
    return () => clearInterval(t);
  }, [sel, loadThread]);
  const listIds = listRows.filter((i) => !i.isGroup).map((i) => i.id).join('|');
  useEffect(() => {
    const ids = listIds ? listIds.split('|') : [];
    const idx = ids.indexOf(sel);
    const t = setTimeout(() => { [ids[idx + 1], ids[idx + 2], ids[idx - 1]].forEach(prefetch); }, 350);
    return () => clearTimeout(t);
  }, [sel, listIds, prefetch]);

  // Live updates: the backend bumps one tiny doc (system_metadata/support_inbox) on every new
  // message, delivery tick or AI draft; reload the list and the open conversation right away.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  useEffect(() => {
    let unsub = null;
    let timer = null;
    let first = true;
    try {
      unsub = onSnapshot(doc(getFirestore(activeFirebaseApp), 'system_metadata', 'support_inbox'), (snap) => {
        if (first) { first = false; return; }
        const pulse = snap.data() || {};
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (tabHidden()) return;
          loadItems();
          const cur = selRef.current;
          if (!cur || notThread(cur)) return;
          loadThread(cur, true);
          if (pulse.kind === 'draft' && pulse.ticketId === cur && !editingRef.current[cur]) setDraftKey((k) => k + 1);
        }, 400);
      }, (err) => console.warn('[inbox] live updates unavailable, using slow refresh:', err.message));
    } catch {
      // No Firebase client here (e.g. tests) — the fallback refresh still runs
    }
    return () => { clearTimeout(timer); if (unsub) unsub(); };
  }, [loadItems, loadThread, activeFirebaseApp]);

  // Presence: tell the team this conversation is open here, and whether we're typing a reply
  const replyingRef = useRef(false);
  replyingRef.current = !!(sel && (compose[sel] || '').trim());
  const replying = replyingRef.current;
  const ping = useCallback(async (id, extra = {}) => {
    try {
      const r = await api(`/api/v1/support/inbox/${id}/presence`, 'POST', { replying: replyingRef.current, ...extra });
      return r.viewers || [];
    } catch {
      return null;
    }
  }, [api]);

  useEffect(() => {
    setViewers([]);
    if (!sel || notThread(sel)) return undefined;
    const id = sel;
    let alive = true;
    const beat = async () => { if (tabHidden()) return; const v = await ping(id); if (alive && v) setViewers(v); };
    beat();
    const t = setInterval(beat, 20000);
    return () => { alive = false; clearInterval(t); ping(id, { leave: true }); };
  }, [sel, ping]);

  // Typing started or stopped: update straight away rather than on the next beat
  useEffect(() => {
    if (sel && !notThread(sel)) ping(sel);
  }, [replying]); // eslint-disable-line react-hooks/exhaustive-deps

  // Email attachments come from the Gmail thread, once per conversation
  const threadSource = thread?.ticket?.source;
  const threadId = thread?.id;
  useEffect(() => {
    if (!threadId || threadSource !== 'email' || atts[threadId]) return;
    setAtts((p) => ({ ...p, [threadId]: [] }));
    api(`/api/v1/support/tickets/${threadId}/email-attachments`)
      .then((d) => setAtts((p) => ({ ...p, [threadId]: d.attachments || [] })))
      .catch(() => {});
  }, [threadId, threadSource, api, atts]);

  const multiChannel = !!(selItem?.otherConversationCount > 0);
  const commCount = thread?.comms?.length || 0;
  useEffect(() => {
    if (!timelineOn || !multiChannel || !sel || notThread(sel)) return undefined;
    let alive = true;
    api(`/api/v1/support/inbox/${sel}/timeline`).then((d) => { if (alive) setTimeline({ forId: sel, data: d }); }).catch(() => {});
    return () => { alive = false; };
  }, [timelineOn, multiChannel, sel, commCount, api]);
  const combined = timelineOn && multiChannel && timeline?.forId === sel && (timeline.data.conversations || []).length > 1;

  const msgsRef = useRef(null);
  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [thread?.comms?.length, thread?.id]);

  // Bumped after any action so the panel refreshes just its brief
  const [briefKey, setBriefKey] = useState(0);
  const refreshAll = useCallback(async () => {
    await Promise.all([loadItems(), loadThread(sel, true)]);
    setBriefKey((k) => k + 1);
  }, [loadItems, loadThread, sel]);

  const select = (id) => { foldCartsFor(id); setSel(id); setMThread(true); setCtxOpen(false); };
  // Change a conversation on screen straight away; the returned function puts it back if saving fails
  const patchItem = (id, patch) => {
    let before = null;
    setItems((prev) => (prev || []).map((i) => { if (i.id !== id) return i; before = i; return { ...i, ...patch }; }));
    return () => { if (before) setItems((prev) => (prev || []).map((i) => (i.id === id ? before : i))); };
  };

  // ---- actions ----
  // Email processing health: a red banner when customer emails stop arriving (checked every 2 minutes)
  const [mailHealth, setMailHealth] = useState(null);
  useEffect(() => {
    if (!perms.tickets) return undefined;
    const check = () => { if (!tabHidden()) api('/api/v1/support/email-health').then((d) => setMailHealth(d.health)).catch(() => {}); };
    check();
    const t = setInterval(check, 120000);
    return () => clearInterval(t);
  }, [api, perms.tickets]);

  // Task on a WhatsApp chat (call to confirm COD, change address…) and the 24 h follow-up template
  const [taskBusy, setTaskBusy] = useState(false);
  // Photo/reel reward: store credit an admin gives after checking what the customer shared
  const [credit, setCredit] = useState(null); // { offers, amount, expiryDays, canGive }
  const [creditConfirm, setCreditConfirm] = useState(null);
  const creditSource = thread?.ticket?.source;
  const threadIdForCredit = thread?.id;
  useEffect(() => {
    setCredit(null);
    setCreditConfirm(null);
    if (creditSource !== 'whatsapp' || !threadIdForCredit) return;
    api(`/api/v1/support/inbox/${threadIdForCredit}/review-credit`).then(setCredit).catch(() => {});
  }, [api, creditSource, threadIdForCredit]);
  const giveCredit = async (offer) => {
    if (creditConfirm !== offer.orderId) { setCreditConfirm(offer.orderId); return; }
    setTaskBusy(true);
    try {
      const r = await api(`/api/v1/support/inbox/${sel}/review-credit`, 'POST', { orderId: offer.orderId, confirm: true });
      toast(`₹${r.amount} store credit given${r.messaged ? ' and the customer was told on WhatsApp' : '. The WhatsApp note could not be sent (chat window closed).'}`);
      setCreditConfirm(null);
      setCredit((c) => ({ ...c, offers: c.offers.map((o) => (o.orderId === offer.orderId ? { ...o, given: true } : o)) }));
      refreshAll();
    } catch (err) { toast(err.message, true); } finally { setTaskBusy(false); }
  };
  const [retrying, setRetrying] = useState(null);
  const retrySend = async (commId) => {
    setRetrying(commId);
    try { await api(`/api/v1/support/inbox/${sel}/comms/${commId}/retry`, 'POST'); toast('Message resent'); refreshAll(); } catch (err) { toast(err.message, true); } finally { setRetrying(null); }
  };
  // Closing a task: the team picks how it ended (saved on the customer action record for analysis)
  const [taskOutcomes, setTaskOutcomes] = useState(null);
  const [closing, setClosing] = useState(null); // { outcome, note }
  useEffect(() => { api('/api/v1/support/task-outcomes').then((d) => setTaskOutcomes(d)).catch(() => {}); }, [api]);
  const doneTask = async () => {
    setTaskBusy(true);
    try { await api(`/api/v1/support/inbox/${sel}/task/done`, 'POST', { outcome: closing?.outcome || null, note: closing?.note || null }); toast('Task done'); setClosing(null); refreshAll(); } catch (err) { toast(err.message, true); } finally { setTaskBusy(false); }
  };
  const sendFollowUp = async () => {
    setTaskBusy(true);
    try { await api(`/api/v1/support/inbox/${sel}/whatsapp-followup`, 'POST', {}); toast('Follow-up template sent. You can chat again once they reply.'); refreshAll(); } catch (err) { toast(err.message, true); } finally { setTaskBusy(false); }
  };

  const setStatus = async (display) => {
    if (!selItem) return;
    const item = selItem;
    const undo = patchItem(item.id, { status: display });
    toast(display === 'resolved' ? `${item.name} resolved` : `Status: ${STATUS_LABEL[display]}`);
    if (display === 'resolved') setMThread(false);
    try {
      if (item.kind === 'conversation') {
        await api(`/api/v1/support/chats/${item.id}/resolve`, 'POST', { resolved: display === 'resolved' });
      } else {
        await api(`/api/v1/support/tickets/${item.id}`, 'PATCH', { status: TO_STORED[display], notifyCustomer: notifyStatus });
      }
      refreshAll();
    } catch (err) {
      undo();
      toast(`Not saved: ${err.message}`, true);
    }
  };

  const assign = async (uid) => {
    if (!selItem) return;
    const m = members.find((x) => x.uid === uid);
    const target = selItem.kind === 'conversation' && selItem.caseId ? selItem.caseId : selItem.id;
    const name = m ? (m.displayName || m.email) : null;
    const undo = patchItem(selItem.id, { assignee: uid ? { ...(selItem.assignee || {}), uid, name } : null });
    toast(uid ? `Assigned to ${name || 'teammate'}` : 'Unassigned');
    try {
      await api(`/api/v1/support/tickets/${target}`, 'PATCH', { assignedTo: uid || null, assignedToName: name });
      loadItems();
      setBriefKey((k) => k + 1);
    } catch (err) {
      undo();
      toast(`Not saved: ${err.message}`, true);
    }
  };

  const snooze = async (untilMs) => {
    if (!selItem) return;
    const item = selItem;
    const undo = patchItem(item.id, { snoozedUntilMs: untilMs || null });
    setSnoozeOpen(false);
    setSnoozeCustom('');
    toast(untilMs ? `Snoozed until ${wakeLabel(untilMs)}. Back sooner if they reply.` : 'Back in the inbox');
    if (untilMs && view !== 'snoozed') { setMThread(false); setSel(null); }
    try {
      await api(`/api/v1/support/inbox/${item.id}/snooze`, 'POST', { untilMs });
      loadItems();
    } catch (err) {
      undo();
      toast(`Not saved: ${err.message}`, true);
    }
  };

  const decideSuggestion = async (decision) => {
    try {
      await api(`/api/v1/support/tickets/${selItem.id}/agent-suggestion/${decision}`, 'POST');
      toast(decision === 'accept' ? 'Closed. No message sent.' : 'Suggestion dismissed');
      refreshAll();
    } catch (err) {
      toast(err.message, true);
    }
  };

  const send = async () => {
    const id = sel;
    const text = (compose[id] || '').trim();
    const files = replyFiles.byId[id] || [];
    if (!text && !files.length) { setSendErr('Write a message or attach a file first.'); return; }
    if (files.some((f) => f.status === 'uploading')) { setSendErr('Wait a moment: files are still uploading.'); return; }
    if (files.some((f) => f.status === 'error')) { setSendErr('Remove the files that failed to upload, then send.'); return; }
    const note = mode[id] === 'note';
    const src = thread?.ticket?.source;
    const problem = channelProblem(note ? 'note' : DM.has(src) ? src : 'email', files);
    if (problem) { setSendErr(problem); return; }
    setSendErr(null);
    const attachments = files.map((f) => f.uploadId);
    const tmpId = `tmp-${Date.now()}`;
    const tmp = { commId: tmpId, pending: true, localFiles: files, content: text, createdAt: new Date().toISOString(), createdByName: 'You', direction: note ? 'internal' : 'outbound', isInternal: note, type: note ? 'internal_note' : (DM.has(thread?.ticket?.source) ? thread.ticket.source : 'email') };
    const dropTmp = () => setThread((cur) => (cur && cur.id === id ? { ...cur, comms: cur.comms.filter((c) => c.commId !== tmpId) } : cur));
    setThread((cur) => (cur && cur.id === id ? { ...cur, comms: [...cur.comms, tmp] } : cur));
    setCompose((p) => ({ ...p, [id]: '' }));
    replyFiles.clear(id);
    try {
      const ed = editing[id];
      if (ed && mode[id] !== 'note') {
        // Edited AI draft: send through the draft so the edit is learned from
        const r = await api(`/api/v1/support/agent/drafts/${ed.draft.draftId}/approve`, 'POST', { replyBody: text, status: ed.status, attachments });
        toast(`Sent · ${STATUS_LABEL[r.status === 'pending' ? 'waiting' : r.status] || 'sent'}`);
        setEditing((p) => ({ ...p, [id]: null }));
        setDraftKey((k) => k + 1);
      } else if (mode[id] === 'note') {
        await api(`/api/v1/support/tickets/${id}/communications`, 'POST', { type: 'internal_note', direction: 'internal', content: text, isInternal: true, attachments });
        toast('Note added');
      } else {
        const type = DM.has(thread?.ticket?.source) ? thread.ticket.source : 'email';
        await api(`/api/v1/support/tickets/${id}/communications`, 'POST', { type, direction: 'outbound', content: text, isInternal: false, attachments });
        toast(`Sent on ${channelLabel(selItem?.channel)}`);
      }
      // keep the bubble (now sent) until the refreshed conversation replaces it, so it never blinks out
      setThread((cur) => (cur && cur.id === id ? { ...cur, comms: cur.comms.map((c) => (c.commId === tmpId ? { ...c, pending: false } : c)) } : cur));
      refreshAll();
    } catch (err) {
      dropTmp();
      // give the text back so nothing typed is lost
      setCompose((p) => ({ ...p, [id]: p[id] ? p[id] : text }));
      replyFiles.restore(id, files);
      setSendErr(`Not sent: ${err.message}`);
    }
  };

  // ---- keyboard ----
  useEffect(() => {
    const onKey = (e) => {
      if (!rootRef.current || !document.body.contains(rootRef.current)) return;
      const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '');
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && document.activeElement?.id === 'cx-composer') { e.preventDefault(); send(); return; }
      if (e.key === 'Escape') { setKeysOpen(false); setMenuOpen(false); setSnoozeOpen(false); setCtxOpen(false); if (typing) document.activeElement.blur(); return; }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const order = isCartView ? cartRows.rows.map((c) => `cart:${c.id}`) : listRows.filter((i) => !i.isGroup).map((i) => i.id);
      const idx = order.indexOf(sel);
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); if (order[idx + 1]) { foldCartsFor(order[idx + 1]); setSel(order[idx + 1]); } }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); if (order[idx - 1]) { foldCartsFor(order[idx - 1]); setSel(order[idx - 1]); } }
      else if (e.key === 'a') rootRef.current.querySelector('[data-cx-approve]')?.click();
      else if (e.key === 'e') { e.preventDefault(); rootRef.current.querySelector('[data-cx-edit]')?.click(); }
      else if (e.key === 'r' && selItem && selItem.status !== 'resolved') setStatus('resolved');
      else if (e.key === 's' && selItem && selItem.status !== 'resolved') { e.preventDefault(); setMenuOpen(false); setSnoozeOpen((v) => !v); }
      else if (e.key === '/') { e.preventDefault(); rootRef.current.querySelector('#cx-search')?.focus(); }
      else if (e.key === '?') setKeysOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // ---- render pieces ----
  // One cart row, in the carts view or mixed into the main list (inList: show it as a due cart)
  const cartRow = (c, inList = false) => (
              <div key={`cart-${c.id}`} className={`cx-row${inList ? ' cx-row-cart cx-row-nested' : ''}`} role="option" tabIndex={0} aria-selected={sel === `cart:${c.id}`} onClick={() => select(`cart:${c.id}`)} onKeyDown={(e) => e.key === 'Enter' && select(`cart:${c.id}`)}>
                <div className="cx-row-av"><ChannelBadge channel="cart" large="xl" /></div>
                <div style={{ minWidth: 0 }}>
                  <div className="cx-row-top"><span className="cx-row-name">{cartName(c)}</span><span className="cx-row-time">{shortTime(new Date(c.createdAt).getTime())}</span></div>
                  <div className="cx-row-prev">{inList ? 'Abandoned cart · ' : ''}{inr(c.cart?.totalPrice)} · {(c.cart?.lineItems || []).length} item{(c.cart?.lineItems || []).length === 1 ? '' : 's'}</div>
                  <div className="cx-row-tags">
                    <span className={`cx-chip ${cartStatus(c) === 'converted' ? 'cx-c-resolved' : cartStatus(c) === 'ignored' ? 'cx-c-plain' : isDue(c) ? 'cx-c-new' : 'cx-c-waiting'}`}>{cartStatus(c) === 'pending' ? (isDue(c) ? 'Follow up now' : 'Not due yet') : cartStatus(c) === 'converted' ? 'Converted' : 'Ignored'}</span>
                    {(c.cart?.totalPrice || 0) >= 3000 && <span className="cx-chip cx-c-case">High value</span>}
                    {c.notes?.length > 0 && <span className="cx-chip cx-c-plain">{c.notes.length} follow-up{c.notes.length === 1 ? '' : 's'}</span>}
                  </div>
                </div>
              </div>
  );
  const count = (v) => (v.cart ? (carts || []).filter((c) => isDue(c)).length : (items || []).filter(v.f).length);
  const selectableViews = allViews.filter((v) => !v.soon);
  const t = thread?.ticket;
  const isDm = DM.has(t?.source);
  const curMode = mode[sel] === 'note' ? 'note' : 'reply';
  const toLabel = isDm ? `Replying on ${channelLabel(selItem?.channel)}` : t?.customerEmail ? `To ${t.customerEmail}` : 'No email address on this conversation';
  const assigneeUid = selItem?.assignee?.uid || '';
  // A cart opened from either list gets the customer panel too (read-only)
  const showCtx = (!isCartView && !!sel && !!t) || !!selCart;

  // Wide screens: name, case and actions live at the top of the customer panel so the conversation runs full height
  const panelHead = docked && t ? (
    <div className="cx-sec cx-ctx-head">
      <div className="cx-ctx-who">
            <ChannelBadge channel={selItem?.channel || 'email'} large="xl" />
            <div className="cx-t-who">
              <b>{selItem?.name || t.customerName}</b>
              <span><span className="cx-contact" title={channelLabel(selItem?.channel)}>{contactOf(selItem, t)}</span>{selItem?.caseRef ? <> · <span className="cx-mono">{selItem.caseRef}</span></> : null} · {selItem?.team || 'Support'}</span>
            </div>
      </div>
            <div className="cx-t-actions">
              <span className="cx-select cx-ph"><select value={assigneeUid} onChange={(e) => assign(e.target.value)} aria-label="Assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.uid} value={m.uid}>{m.displayName || m.email}{m.uid === me ? ' (you)' : ''}</option>)}
              </select><Icon name="down" /></span>
              {selItem?.kind === 'ticket' && (
                <span className="cx-select cx-ph"><select value={selItem.status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                  {selItem.status === 'self_service' && <option value="self_service" disabled>Self-service</option>}{Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select><Icon name="down" /></span>
              )}
              {selItem && selItem.status !== 'resolved' && (
                isSnoozed(selItem)
                  ? <button className="cx-btn cx-ph" onClick={() => snooze(null)}><Icon name="snooze" />Unsnooze</button>
                  : <button className="cx-btn cx-ph" onClick={() => { setMenuOpen(false); setSnoozeOpen((v) => !v); }} aria-expanded={snoozeOpen}><Icon name="snooze" />Snooze</button>
              )}
              {snoozeOpen && (
                <div className="cx-menu cx-snooze-menu" role="menu" aria-label="Snooze until">
                  {snoozeOptions().map(([label, d]) => (
                    <button key={label} onClick={() => snooze(d.getTime())}>{label}<small>{wakeLabel(d.getTime())}</small></button>
                  ))}
                  <div className="cx-snooze-custom">
                    <input type="datetime-local" value={snoozeCustom} onChange={(e) => setSnoozeCustom(e.target.value)} aria-label="Custom snooze time" />
                    <button className="cx-btn cx-btn-sm" disabled={!snoozeCustom} onClick={() => snooze(new Date(snoozeCustom).getTime())}>Set</button>
                  </div>
                </div>
              )}
              {selItem?.status !== 'resolved'
                ? <button className="cx-btn cx-btn-primary" onClick={() => setStatus('resolved')}><Icon name="check" />Resolve</button>
                : <button className="cx-btn" onClick={() => setStatus(selItem.kind === 'conversation' ? 'waiting' : 'open')}><Icon name="refresh" />Reopen</button>}
              <button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => { setSnoozeOpen(false); setMenuOpen((v) => !v); }} aria-label="More actions" aria-expanded={menuOpen}><Icon name="more" /></button>
              {menuOpen && (
                <div className="cx-menu" role="menu">
                  {selItem?.kind === 'ticket' && (
                    <label><input type="checkbox" checked={notifyStatus} onChange={(e) => setNotifyStatus(e.target.checked)} /> Email customer when status changes</label>
                  )}
                  {selItem && selItem.status !== 'resolved' && (
                    isSnoozed(selItem)
                      ? <button onClick={() => { setMenuOpen(false); snooze(null); }}><Icon name="snooze" size={16} />Unsnooze</button>
                      : <button onClick={() => { setMenuOpen(false); setSnoozeOpen(true); }}><Icon name="snooze" size={16} />Snooze</button>
                  )}
                  <button onClick={() => { setMenuOpen(false); reportProblem({ where: 'chat' }); }}><Icon name="flag" size={16} />Report a problem</button>
                  <button onClick={() => { setKeysOpen(true); setMenuOpen(false); }}><Icon name="keyboard" size={16} />Keyboard shortcuts</button>
                </div>
              )}
            </div>
    </div>
  ) : null;

  const renderMessages = () => {
    let lastDay = null;
    const out = [];
    // Files on an internal note (stored with the note, shown like customer media)
    const noteFiles = (c) => {
      const items = (c.attachments || []).map((a, idx) => ({ a, idx })).filter(({ a }) => a.storagePath);
      if (!items.length) return null;
      const t = c.ticketId || c.conversationId || thread.id;
      return <div className="cx-att-row">{items.map(({ a, idx }) => <InboxAttachment key={idx} att={a} ticketId={t} fetchBlob={fetchBlob} path={`/api/v1/support/tickets/${t}/wa-media/${c.commId}/${idx}`} />)}</div>;
    };
    // Put each Gmail attachment under the customer email it arrived with (closest in time)
    const byComm = {};
    const loose = [];
    const inbound = (thread?.comms || []).filter((c) => c.direction === 'inbound' && !DM.has(c.type));
    (atts[thread?.id] || []).forEach((a) => {
      let best = null;
      inbound.forEach((c) => {
        const d = Math.abs(tsMs(c.createdAt) - a.internalDateMs);
        if (!best || d < best.d) best = { id: c.commId, d };
      });
      if (best) (byComm[best.id] = byComm[best.id] || []).push(a); else loose.push(a);
    });
    if (loose.length) {
      out.push(<div className="cx-att-loose" key="att-loose"><span className="cx-hint">Attachments in this email thread</span><div className="cx-att-row">{loose.map((a) => <InboxAttachment key={`${a.gmailMsgId}-${a.partId}`} att={a} ticketId={thread.id} fetchBlob={fetchBlob} />)}</div></div>);
    }
    const list = combined ? timeline.data.comms : (thread?.comms || []);
    // Email subject at the top, like Gmail: customers often write the whole complaint there
    const subjectOf = (x) => String(x || '').replace(/^\s*((re|fwd?|fw)\s*:\s*)+/i, '').replace(/\s*\[TICK-\d+\]\s*/g, ' ').trim();
    const mainSubject = !combined && thread?.ticket?.subject && !DM.has(thread.ticket.source) ? subjectOf(thread.ticket.subject) : '';
    if (mainSubject) out.push(<h3 className="cx-subject" key="subject">{mainSubject}</h3>);
    list.forEach((c) => {
      const at = tsMs(c.createdAt);
      // In the combined view, say where each message came from
      const fromHere = !combined || c.conversationId === sel;
      const where = combined ? <span className={`cx-where${fromHere ? '' : ' other'}`}><ChannelBadge channel={SRC_CH[c.source] || 'email'} />{c.ref ? <span className="cx-mono">{c.ref}</span> : null}</span> : null;
      const day = at ? dayLabel(at) : null;
      if (day && day !== lastDay) { out.push(<div className="cx-day" key={`d-${c.commId}`}>{day}</div>); lastDay = day; }
      const time = at ? new Date(at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
      // Automatic "we've received your message" email: a quiet system line, not a reply bubble
      if (c.systemLine) {
        out.push(<div className="cx-sysline" key={c.commId}><Icon name="check" size={13} /><span>{c.content}</span><span className="cx-sysline-t">{time}</span></div>);
        return;
      }
      if (c.autoAck) {
        out.push(<div className="cx-sysline" key={c.commId}><Icon name="send" size={13} /><span>{c.content}</span><span className="cx-sysline-t">{time}</span></div>);
        return;
      }
      if (c.isInternal || c.direction === 'internal') {
        out.push(<div className="cx-msg cx-note" key={c.commId}><div className="cx-bubble"><Icon name="lock" size={14} /> {/^\[(image|video|document)[^\]]*\]$/.test(c.content || '') && c.attachments?.length ? '' : c.content}<PendingFiles files={c.localFiles} />{noteFiles(c)}</div><div className="cx-meta">{where}Internal note · {c.createdByName || 'Team'} · {time}</div></div>);
        return;
      }
      const dir = c.direction === 'inbound' ? 'in' : 'out';
      const autoMsg = dir === 'out' && c.automated;
      const mail = !DM.has(c.type) && c.type !== 'chat';
      // A reply that changed the subject shows its own subject line
      const ownSubject = mail && dir === 'in' && c.subject && subjectOf(c.subject) && subjectOf(c.subject) !== mainSubject ? subjectOf(c.subject) : null;
      const isHtml = !!(c.contentHtml && dir === 'in');
      const parts = mail ? (isHtml ? splitHtml(c.contentHtml) : splitText(c.content)) : { main: c.content, quoted: null };
      const full = !!showQuoted[c.commId];
      const waMedia = (c.attachments || []).map((a, idx) => ({ a, idx })).filter(({ a }) => a.storagePath || a.mediaId);
      // A WhatsApp photo / voice note without caption: the file itself is the message
      const mediaOnly = waMedia.length && /^\[(image|video|audio|voice note|document|sticker)[^\]]*\]$/.test(String(c.content || '').trim());
      const shown = mediaOnly ? null : (full && parts.quoted ? parts.quoted : parts.main);
      const commTicket = c.ticketId || c.conversationId || thread.id;
      const attach = [
        ...(c.attachments || []).filter((a) => a.url).map((a) => <div key={a.url}><a className="cx-attach" href={a.url} target="_blank" rel="noreferrer"><Icon name="attach" size={14} />Open {a.type}</a></div>),
        ...(waMedia.length ? [<div className="cx-att-row" key="wa">{waMedia.map(({ a, idx }) => (
          <InboxAttachment key={idx} att={a} ticketId={commTicket} fetchBlob={fetchBlob} path={`/api/v1/support/tickets/${commTicket}/wa-media/${c.commId}/${idx}`} />
        ))}</div>] : []),
        ...((byComm[c.commId] || []).length ? [<div className="cx-att-row" key="gm">{byComm[c.commId].map((a) => <InboxAttachment key={`${a.gmailMsgId}-${a.partId}`} att={a} ticketId={thread.id} fetchBlob={fetchBlob} />)}</div>] : []),
      ];
      out.push(
        <div className={`cx-msg ${dir}${mail ? ' mail' : ''}${autoMsg ? ' auto' : ''}${fromHere ? '' : ' elsewhere'}`} key={c.commId}>
          {isHtml
            ? <div className="cx-bubble cx-bubble-html">{ownSubject && <b className="cx-msg-subject">{ownSubject}</b>}<div dangerouslySetInnerHTML={{ __html: shown }} />{attach}</div>
            : <div className="cx-bubble">{ownSubject && <b className="cx-msg-subject">{ownSubject}</b>}{autoMsg && c.imageUrl && <img className="cx-auto-img" src={c.imageUrl} alt="" loading="lazy" />}{c.waAction && <span className="cx-btn-tag">Button</span>}{mail ? shown : <WaText text={shown} />}<PendingFiles files={c.localFiles} />{attach}
              {autoMsg && c.buttons?.length > 0 && <span className="cx-auto-btns">{c.buttons.map((b) => <span key={b}>{b}</span>)}</span>}</div>}
          {parts.quoted && (
            <button className="cx-quote-toggle" onClick={() => setShowQuoted((p) => ({ ...p, [c.commId]: !full }))} aria-expanded={full}>
              <Icon name="more" size={14} />{full ? 'Hide earlier email' : 'Show earlier email'}
            </button>
          )}
          <div className="cx-meta">{dir === 'out' && !c.pending && <FlagButton label="Report this message" onClick={() => reportProblem({ where: 'message', type: 'wrong_message', commId: c.commId, snapshot: { message: c.content || '', label: c.automationLabel || '' } })} />}{where}{autoMsg ? `Automated · ${c.automationLabel || 'WhatsApp'} · ` : dir === 'out' ? `${c.createdByName || 'Team'} · ` : ''}{time}
            {c.pending ? <><WaTicks status={null} /> Sending…</> : dir === 'out' && c.type === 'whatsapp' && c.waStatus !== 'failed' && (c.waStatus || c.metaMessageId) ? <WaTicks status={c.waStatus} at={c.waStatusAt} /> : null}
            {dir === 'out' && c.waStatus === 'failed' ? (
              <span className="cx-wa-fail" title={c.waError?.details || ''}>
                <Icon name="alert" size={13} />Not delivered{c.waError?.title ? `: ${c.waError.title}` : ''}
                {c.retriedAt ? <span className="cx-wa-resent"> · Resent</span>
                  : !c.automated && <button className="cx-linkbtn cx-wa-retry" onClick={() => retrySend(c.commId)} disabled={retrying === c.commId}>{retrying === c.commId ? 'Resending…' : 'Retry'}</button>}
              </span>
            ) : null}
          </div>
        </div>,
      );
    });
    return out;
  };

  return (
    <div
      ref={rootRef}
      className={`cx${ctxOpen ? ' ctx-open' : ''}${mThread ? ' m-thread' : ''}${showCtx ? '' : ' no-ctx'}${viewsOff ? ' views-off' : ''}`}
      style={{ ...themeStyle(accent), height: fill ? '100%' : height, ...(listW ? { '--list-w': `${listW}px` } : {}) }}
    >
      <aside className="cx-views" aria-label="Views">
        {(onNewTicket || onArchive) && (
          <div className="cx-views-actions">
            {onNewTicket && <button className="cx-btn cx-btn-primary" onClick={onNewTicket}><Icon name="plus" size={16} />New ticket</button>}
          </div>
        )}
        <div className="cx-views-h"><span className="cx-views-title"><button className="cx-btn cx-btn-icon cx-btn-ghost cx-views-btn" onClick={toggleViews} aria-label="Hide views" title="Hide views"><Icon name="layout" size={16} /></button>Inbox</span> <span className={`cx-agent-pill${agentOn ? ' on' : ''}`} title={agentOn ? 'AI agent drafts replies. Nothing sends without approval.' : 'AI agent is off'}><i />AI agent {agentOn ? 'on' : 'off'}</span></div>
        {views.map((g) => (
          <div className="cx-vgroup" key={g.group}>
            <div className="cx-vgroup-label">{g.group}</div>
            {g.items.map((v) => (
              <button key={v.id} className="cx-vitem" aria-current={view === v.id} onClick={() => {
                if (v.soon) { toast('WhatsApp connects in the next phase'); return; }
                setView(v.id); setTab('open'); setMThread(false); setSel(null);
              }}>
                {v.ch ? <ChannelBadge channel={v.ch} /> : <Icon name={v.icon} size={16} />}
                <span>{v.label}</span>
                {v.soon ? <span className="cx-soon">Soon</span> : <span className="cx-count">{count(v)}</span>}
              </button>
            ))}
          </div>
        ))}
        {onArchive && (
          <div className="cx-views-foot">
            <button className="cx-vitem" onClick={onArchive}><Icon name="archive" size={16} /><span>Archive old tickets</span></button>
          </div>
        )}
      </aside>

      <section className="cx-list" aria-label={isCartView ? 'Recoverable carts' : 'Conversations'}>
        {mailHealth?.problem && <div className="cx-health" role="alert"><Icon name="alert" size={16} /><span>{mailHealth.problem}</span></div>}
        {mailHealth?.waProblem && <div className="cx-health" role="alert"><Icon name="alert" size={16} /><span>{mailHealth.waProblem}</span></div>}
        <div className="cx-list-head">
          <div className="cx-list-title">
            {viewsOff && <button className="cx-btn cx-btn-icon cx-btn-ghost cx-views-btn" onClick={toggleViews} aria-label="Show views" title="Show views"><Icon name="layout" size={16} /></button>}
            <h2>{current?.label || 'Inbox'}</h2>
            {viewsOff && onNewTicket && <button className="cx-btn cx-btn-icon cx-btn-ghost cx-views-btn" onClick={onNewTicket} aria-label="New ticket" title="New ticket"><Icon name="plus" size={16} /></button>}{isCartView && <button className="cx-btn cx-btn-icon cx-btn-ghost cx-views-btn" onClick={syncCarts} disabled={cartSync} aria-label="Sync carts from Shopify" title="Sync carts from Shopify"><Icon name="refresh" size={15} /></button>}<span>{isCartView ? `${cartRows.tabs.open.length} open` : `${tabs.open.length + tabs.waiting.length} open${current?.id === 'all' && dueCarts.length ? ` · ${dueCarts.length} cart${dueCarts.length === 1 ? '' : 's'} due` : ''}`}</span></div>
          <div className="cx-view-select">
            <span className="cx-select"><select value={view} onChange={(e) => { setView(e.target.value); setTab('open'); setSel(null); }} aria-label="View">
              {selectableViews.map((v) => <option key={v.id} value={v.id}>{v.label} ({count(v)})</option>)}
            </select><Icon name="down" /></span>
          </div>
          <label className="cx-search"><Icon name="search" size={16} /><input id="cx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={isCartView ? 'Search name, email, phone' : 'Search name, order, case'} aria-label="Search" /><kbd className="cx-kbd">/</kbd></label>
          {isCartView ? (
            <div className="cx-seg" role="group" aria-label="Cart status">
              {[['due', 'Due'], ['open', 'All open'], ['converted', 'Converted'], ['ignored', 'Ignored']].map(([id, l]) => (
                <button key={id} aria-pressed={cartTab === id} onClick={() => { setCartTab(id); setSel(null); }}>{l}<span className="cx-num">{cartRows.tabs[id].length}</span></button>
              ))}
            </div>
          ) : view === 'snoozed' ? (
            <span className="cx-hint">Each one comes back at its time, or sooner if the customer replies.</span>

          ) : (
            <div className="cx-seg" role="group" aria-label="Status">
              {[['open', 'Open'], ['waiting', 'Waiting'], ['self', 'Self-service'], ['resolved', 'Resolved']].map(([id, l]) => (
                <button key={id} aria-pressed={tab === id} onClick={() => { setTab(id); setSel(null); }}>{l}<span className="cx-num">{tabs[id].length}</span></button>
              ))}
            </div>
          )}
        </div>
        <div className="cx-rows" role="listbox" aria-label="List">
          {isCartView ? (
            carts === null ? <><div className="cx-skel" /><div className="cx-skel" /></> : cartRows.rows.length === 0 ? (
              <div className="cx-empty"><strong>No carts here</strong>{cartTab === 'due' ? 'Nothing is due for follow-up right now.' : 'Carts will appear here as customers leave checkout.'}</div>
            ) : cartRows.rows.map((c) => cartRow(c))
          ) : items === null ? <><div className="cx-skel" /><div className="cx-skel" /><div className="cx-skel" /></> : listRows.length === 0 ? (
            <div className="cx-empty"><strong>{q ? 'No matches' : view === 'snoozed' ? 'Nothing snoozed' : tab === 'self' ? 'No self-service chats' : tab === 'resolved' ? 'Nothing resolved yet' : 'All caught up'}</strong>{q ? 'Try a name, order number or case.' : view === 'snoozed' ? 'Snooze a conversation to hide it until a set time.' : tab === 'self' ? 'Chats where the customer only tapped WhatsApp buttons (order status, new arrivals, policy). They move to Open as soon as the customer types, sends a photo or asks for the team.' : tab === 'resolved' ? 'Resolved conversations collect here.' : 'New messages from every channel land here.'}</div>
          ) : listRows.map((i) => (i.id === 'ws-group' ? (
            <button key="ws-group" type="button" className={`cx-row cx-row-group${wsGroup.open ? ' open' : ''}`} onClick={() => setWsOpen((v) => !v)} aria-expanded={wsGroup.open}>
              <div className="cx-row-av"><span className="cx-ws-av" aria-hidden="true"><Icon name="shop" size={18} /></span></div>
              <div style={{ minWidth: 0 }}>
                <div className="cx-row-top">{wsGroup.overdue > 0 && <i className="cx-urg" title="Past the promised 48 hours" />}<span className="cx-row-name">Wholesale registrations</span><span className="cx-row-time">{shortTime(wsGroup.newestMs)}</span></div>
                <div className="cx-row-prev">{wsGroup.list.length} waiting for approval</div>
                <div className="cx-row-tags">
                  {wsGroup.overdue > 0 && <span className="cx-chip cx-c-new">{wsGroup.overdue} overdue</span>}
                  <span className="cx-chip cx-c-plain">oldest {wsGroup.oldestH >= 48 ? `${Math.floor(wsGroup.oldestH / 24)} days` : `${wsGroup.oldestH} h`}</span>
                </div>
              </div>
              <Icon name="down" size={16} className={`cx-row-chev${wsGroup.open ? ' cx-rot180' : ''}`} />
            </button>
          ) : i.isWs ? (
            <div key={i.id} className="cx-row cx-row-cart cx-row-nested" role="option" tabIndex={0} aria-selected={sel === i.id} onClick={() => select(i.id)} onKeyDown={(e) => e.key === 'Enter' && select(i.id)}>
              <div className="cx-row-av"><span className="cx-ws-av" aria-hidden="true"><Icon name="shop" size={18} /></span></div>
              <div style={{ minWidth: 0 }}>
                <div className="cx-row-top"><span className="cx-row-name">{i.ws.company || i.ws.name}</span><span className="cx-row-time">{shortTime(Date.parse(i.ws.createdAt))}</span></div>
                <div className="cx-row-prev">{[i.ws.name !== i.ws.company ? i.ws.name : null, i.ws.city].filter(Boolean).join(' · ') || 'Wholesale registration'}</div>
                <div className="cx-row-tags">
                  <span className={`cx-chip ${i.ws.ageHours > 48 ? 'cx-c-new' : 'cx-c-waiting'}`}>Waiting {i.ws.ageHours >= 48 ? `${Math.floor(i.ws.ageHours / 24)} days` : `${i.ws.ageHours} h`}</span>
                  {i.ws.status === 'asked' && <span className="cx-chip cx-c-plain">Details asked</span>}
                  {i.ws.notes?.length > 0 && <span className="cx-chip cx-c-plain">{i.ws.notes.length} note{i.ws.notes.length === 1 ? '' : 's'}</span>}
                </div>
              </div>
            </div>
          ) : i.isGroup ? (
            <button key="cart-group" type="button" className={`cx-row cx-row-group${cartGroup.open ? ' open' : ''}`} onClick={toggleCarts} aria-expanded={cartGroup.open}>
              <div className="cx-row-av"><ChannelBadge channel="cart" large="xl" /></div>
              <div style={{ minWidth: 0 }}>
                <div className="cx-row-top"><span className="cx-row-name">Abandoned carts</span><span className="cx-row-time">{shortTime(cartGroup.newestMs)}</span></div>
                <div className="cx-row-prev">{cartGroup.list.length} due · {inr(cartGroup.total)}</div>
                <div className="cx-row-tags">
                  {cartGroup.fresh > 0 && !cartGroup.open && <span className="cx-chip cx-c-new">{cartGroup.fresh} new</span>}
                  {cartGroup.high > 0 && <span className="cx-chip cx-c-case">{cartGroup.high} high value</span>}
                  <span className="cx-chip cx-c-plain">oldest due {shortTime(cartGroup.oldestMs)}</span>
                </div>
              </div>
              <Icon name="down" size={16} className={`cx-row-chev${cartGroup.open ? ' cx-rot180' : ''}`} />
            </button>
          ) : i.isCart ? cartRow(i.cart, true) : (
            <div key={i.id} className={`cx-row${i.unread ? ' unread' : ''}`} role="option" tabIndex={0} aria-selected={sel === i.id} onClick={() => select(i.id)} onKeyDown={(e) => e.key === 'Enter' && select(i.id)}>
              <div className="cx-row-av"><ChannelBadge channel={i.channel} large="xl" /></div>
              <div style={{ minWidth: 0 }}>
                <div className="cx-row-top">{i.urgent && <i className="cx-urg" title="Urgent" />}<span className="cx-row-name">{i.name}</span>{i.sendFailed && <span className="cx-row-fail" title={`Last WhatsApp message not delivered: ${i.sendFailed}`}><Icon name="alert" size={14} /></span>}<span className="cx-row-time">{shortTime(i.lastActivityMs)}</span></div>
                <div className="cx-row-prev">{i.task?.type === 'cod_call' ? `Call to confirm COD · ${i.task.orderNumber || ''}${i.task.amount ? ` · ${i.task.amount}` : ''}` : <>{i.previewDirection === 'outbound' ? 'You: ' : ''}{i.subject && i.channel === 'email' ? `${i.subject} · ` : ''}{i.preview}</>}</div>
                <div className="cx-row-tags">
                  <span className={`cx-chip cx-c-${i.status}`}>{CHIP_LABEL[i.status]}</span>
                  {i.task && <span className="cx-chip cx-c-task"><Icon name={i.task.type === 'cod_call' ? 'phone' : 'alert'} />Task · {i.task.label}</span>}
                  {i.draftStatus === 'pending' && !NOT_OPEN.has(i.status) && <span className="cx-chip cx-c-ai"><Icon name="spark" />AI draft</span>}
                  {i.caseRef && <span className="cx-chip cx-c-case cx-mono">{i.caseRef}</span>}
                  {i.orderNumber && <span className="cx-chip cx-c-case cx-mono" title="Pinned order">#{i.orderNumber}</span>}
                  {!NOT_OPEN.has(i.status) && <WindowChip expiresMs={i.replyWindowExpiresMs} strict={i.channel === 'wa'} />}
                  {i.suggestClose && <span className="cx-chip cx-c-plain">Suggest close</span>}
                  {i.automatedSender && <span className="cx-chip cx-c-plain">Automated sender</span>}
                  {i.otherConversationCount > 0 && <span className="cx-more-ch" title={`Same customer on ${i.otherChannels.map((c) => channelLabel(c)).join(', ')}`}>{i.otherChannels.slice(0, 3).map((c) => <ChannelBadge key={c} channel={c} />)}+{i.otherConversationCount}</span>}
                  {isSnoozed(i) && <span className="cx-chip cx-c-plain"><Icon name="snooze" />Until {wakeLabel(i.snoozedUntilMs)}</span>}
                  {i.abandonedFlow && <span className="cx-chip cx-c-waiting" title="Opened the menu but didn't pick a reason. Maybe worth a call.">Started {i.abandonedFlow === 'cancel' ? 'cancel' : 'return'}, didn't finish</span>}
                  {i.viewers?.length > 0 && <span className="cx-row-eye" title={i.viewers.map((v) => `${v.name}${v.replying ? ' is replying' : ' is viewing'}`).join(', ')}><Icon name="eye" />{firstName(i.viewers[0].name)}{i.viewers.length > 1 ? ` +${i.viewers.length - 1}` : ''}</span>}
                  {i.assignee && <span className="cx-avatar mini" title={i.assignee.name}>{initials(i.assignee.name)}</span>}
                  {cartByItem[i.id] && <span className="cx-chip cx-c-waiting" title="This customer has an open cart"><Icon name="cart" />Cart {inr(cartByItem[i.id].cart?.totalPrice)}</span>}
                </div>
              </div>
            </div>
          )))}
        </div>
        <div className="cx-resize" role="separator" aria-orientation="vertical" aria-label="Drag to resize the list. Double-click to reset." title="Drag to resize · double-click to reset" onPointerDown={startResize} onDoubleClick={resetWidth} />
      </section>

      {selWs ? (
        <InboxWholesale item={selWs} api={api} toast={toast} onBack={() => setMThread(false)} onChanged={loadWholesale} />
      ) : isCartView || selCart ? (
        <InboxCart cart={selCart} api={api} toast={toast} onBack={() => setMThread(false)} onChanged={loadCarts} onCustomer={docked ? null : () => setCtxOpen((v) => !v)} />
      ) : !sel ? (
        <section className="cx-thread"><div className="cx-empty"><strong>Select a conversation</strong>Pick one from the list to read and reply.</div></section>
      ) : threadErr ? (
        <section className="cx-thread"><div className="cx-empty"><strong>Couldn't open this conversation</strong>{threadErr}</div></section>
      ) : !t ? (
        <section className="cx-thread" aria-busy="true"><div className="cx-skel" style={{ height: 62, margin: 0, borderRadius: 0 }} /><div className="cx-skel" /><div className="cx-skel" /></section>
      ) : (
        <section className="cx-thread" aria-label={`Conversation with ${selItem?.name || t.customerName}`}>
          {!docked && (
          <header className="cx-t-head">
            <button className="cx-btn cx-btn-icon cx-btn-ghost cx-back" onClick={() => setMThread(false)} aria-label="Back to list"><Icon name="back" /></button>
            <ChannelBadge channel={selItem?.channel || 'email'} large />
            <div className="cx-t-who">
              <b>{selItem?.name || t.customerName}</b>
              <span><span className="cx-contact" title={channelLabel(selItem?.channel)}>{contactOf(selItem, t)}</span>{selItem?.caseRef ? <> · <span className="cx-mono">{selItem.caseRef}</span></> : null} · {selItem?.team || 'Support'}</span>
            </div>
            <div className="cx-t-actions">
              {selItem?.automatedSender && <button className="cx-btn cx-btn-sm" onClick={notBot} title="Marked as another business's automated WhatsApp. Undo if this is a real customer.">Automated sender · Not a bot</button>}
              <span className="cx-select cx-hide-sm"><select value={assigneeUid} onChange={(e) => assign(e.target.value)} aria-label="Assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.uid} value={m.uid}>{m.displayName || m.email}{m.uid === me ? ' (you)' : ''}</option>)}
              </select><Icon name="down" /></span>
              {selItem?.kind === 'ticket' && (
                <span className="cx-select cx-hide-sm"><select value={selItem.status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                  {selItem.status === 'self_service' && <option value="self_service" disabled>Self-service</option>}{Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select><Icon name="down" /></span>
              )}
              {selItem && selItem.status !== 'resolved' && (
                isSnoozed(selItem)
                  ? <button className="cx-btn cx-hide-sm" onClick={() => snooze(null)}><Icon name="snooze" />Unsnooze</button>
                  : <button className="cx-btn cx-hide-sm" onClick={() => { setMenuOpen(false); setSnoozeOpen((v) => !v); }} aria-expanded={snoozeOpen}><Icon name="snooze" />Snooze</button>
              )}
              {snoozeOpen && (
                <div className="cx-menu cx-snooze-menu" role="menu" aria-label="Snooze until">
                  {snoozeOptions().map(([label, d]) => (
                    <button key={label} onClick={() => snooze(d.getTime())}>{label}<small>{wakeLabel(d.getTime())}</small></button>
                  ))}
                  <div className="cx-snooze-custom">
                    <input type="datetime-local" value={snoozeCustom} onChange={(e) => setSnoozeCustom(e.target.value)} aria-label="Custom snooze time" />
                    <button className="cx-btn cx-btn-sm" disabled={!snoozeCustom} onClick={() => snooze(new Date(snoozeCustom).getTime())}>Set</button>
                  </div>
                </div>
              )}
              <button className="cx-btn cx-hide-sm" onClick={() => reportProblem({ where: 'chat' })} title="Something wrong here? Report it"><Icon name="flag" />Report a problem</button>
              {selItem?.status !== 'resolved'
                ? <button className="cx-btn" onClick={() => setStatus('resolved')}><Icon name="check" />Resolve</button>
                : <button className="cx-btn" onClick={() => setStatus(selItem.kind === 'conversation' ? 'waiting' : 'open')}><Icon name="refresh" />Reopen</button>}
              <button className="cx-btn cx-btn-icon cx-ctx-toggle" onClick={() => setCtxOpen((v) => !v)} aria-label="Customer details"><Icon name="user" /></button>
              <button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => { setSnoozeOpen(false); setMenuOpen((v) => !v); }} aria-label="More actions" aria-expanded={menuOpen}><Icon name="more" /></button>
              {menuOpen && (
                <div className="cx-menu" role="menu">
                  {selItem?.kind === 'ticket' && (
                    <label><input type="checkbox" checked={notifyStatus} onChange={(e) => setNotifyStatus(e.target.checked)} /> Email customer when status changes</label>
                  )}
                  {selItem && selItem.status !== 'resolved' && (
                    isSnoozed(selItem)
                      ? <button onClick={() => { setMenuOpen(false); snooze(null); }}><Icon name="snooze" size={16} />Unsnooze</button>
                      : <button onClick={() => { setMenuOpen(false); setSnoozeOpen(true); }}><Icon name="snooze" size={16} />Snooze</button>
                  )}
                  <button onClick={() => { setMenuOpen(false); reportProblem({ where: 'chat' }); }}><Icon name="flag" size={16} />Report a problem</button>
                  <button onClick={() => { setKeysOpen(true); setMenuOpen(false); }}><Icon name="keyboard" size={16} />Keyboard shortcuts</button>
                </div>
              )}
            </div>
          </header>
          )}
          {viewers.length > 0 && (
            <div className="cx-viewing" role="status">
              <span className="cx-pulse" />
              {viewers.slice(0, 3).map((v) => <span key={v.uid} className="cx-avatar" title={v.name}>{initials(v.name)}</span>)}
              <span>
                {viewers.map((v) => firstName(v.name)).join(' and ')} {viewers.length > 1 ? 'are' : 'is'} {viewers.some((v) => v.replying) ? 'replying' : 'viewing'} this conversation
                {viewers.some((v) => v.replying) ? '. Check before you send so the customer does not get two answers.' : ''}
              </span>
            </div>
          )}
          {selItem && isSnoozed(selItem) && (
            <div className="cx-banner"><Icon name="snooze" size={16} />Snoozed until {wakeLabel(selItem.snoozedUntilMs)}. It comes back sooner if the customer replies.
              <button className="cx-btn cx-btn-sm" onClick={() => snooze(null)}>Unsnooze</button>
            </div>
          )}
          {t?.task && (
            <div className="cx-banner cx-task-banner" role="status"><Icon name={t.task.type === 'cod_call' ? 'phone' : 'alert'} size={16} />
              <span><b>{t.task.label}</b>{t.task.orderNumber ? ` · ${t.task.orderNumber}` : ''}{t.task.amount ? ` · ${t.task.amount}` : ''}{TASK_HINT[t.task.type] ? ` · ${TASK_HINT[t.task.type]}` : ''}</span>
              {t.task.type === 'cod_call' && t.customerPhone && <a className="cx-btn cx-btn-sm" href={`tel:${t.customerPhone}`}><Icon name="phone" size={14} />Call</a>}
              {!closing && <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={() => setClosing({ outcome: null, note: '' })}>Mark done</button>}
              {closing && (() => {
                const choices = taskOutcomes?.outcomes?.[t.task.type] || { DONE: 'Done' };
                const needNote = (taskOutcomes?.noteRequired || []).includes(closing.outcome);
                return (
                  <div className="cx-task-close" role="group" aria-label="How did it end?">
                    <span className="cx-hint">How did it end?</span>
                    {Object.entries(choices).map(([code, label]) => (
                      <label key={code} className="cx-task-opt"><input type="radio" name="cx-task-outcome" checked={closing.outcome === code} onChange={() => setClosing((c) => ({ ...c, outcome: code }))} /> {label}</label>
                    ))}
                    <input className="cx-task-note" type="text" maxLength={300} placeholder={needNote ? 'Why? (required)' : 'Note (optional)'} aria-label="Note" value={closing.note} onChange={(e) => setClosing((c) => ({ ...c, note: e.target.value }))} />
                    <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={doneTask} disabled={taskBusy || !closing.outcome || (needNote && !closing.note.trim())}>{taskBusy ? 'Saving…' : 'Save'}</button>
                    <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setClosing(null)}>Cancel</button>
                  </div>
                );
              })()}
            </div>
          )}
          {(() => {
            // Offer the reward only once the customer has actually shared a photo, video or an Instagram link
            const shared = (thread?.comms || []).some((c) => c.direction === 'inbound' && ((c.attachments || []).some((a) => /image|video/.test(a.type || a.mimeType || '')) || /instagram\.com|instagr\.am/i.test(c.content || '')));
            const open = (credit?.offers || []).filter((o) => !o.given);
            if (!shared || !open.length) return null;
            return open.map((o) => (
              <div key={o.orderId} className="cx-banner cx-credit-banner" role="status"><Icon name="spark" size={16} />
                <span>Photo/reel reward · ₹{credit.amount} store credit for <b>{o.orderNumber}</b>, valid {credit.expiryDays} days. Check what they shared first.{o.lastError ? ` Last try failed: ${o.lastError}` : ''}</span>
                {credit.canGive
                  ? <button className={`cx-btn cx-btn-sm${creditConfirm === o.orderId ? ' cx-btn-primary' : ''}`} onClick={() => giveCredit(o)} disabled={taskBusy}>{creditConfirm === o.orderId ? `Confirm: give ₹${credit.amount}` : `Give ₹${credit.amount} credit`}</button>
                  : <span className="cx-hint">An admin can give the credit</span>}
                {creditConfirm === o.orderId && <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setCreditConfirm(null)}>Cancel</button>}
              </div>
            ));
          })()}
          {selItem?.channel === 'wa' && selItem.replyWindowExpiresMs && selItem.replyWindowExpiresMs < Date.now() && selItem.status !== 'resolved' && (
            <div className="cx-banner"><Icon name="clock" size={16} />More than 24 hours since the customer's last WhatsApp message. Only an approved template can start the chat again.
              <button className="cx-btn cx-btn-sm" onClick={sendFollowUp} disabled={taskBusy}>Send follow-up template</button>
            </div>
          )}
          {selItem?.suggestClose && (
            <div className="cx-banner"><Icon name="spark" size={16} />AI suggests closing: {selItem.suggestClose}
              <button className="cx-btn cx-btn-sm" onClick={() => decideSuggestion('accept')}>Close, no message</button>
              <button className="cx-btn cx-btn-ghost cx-btn-sm" style={{ marginLeft: 0 }} onClick={() => decideSuggestion('dismiss')}>Dismiss</button>
            </div>
          )}
          {multiChannel && (
            <div className="cx-tl-bar">
              <div className="cx-seg" role="group" aria-label="Messages to show">
                <button aria-pressed={!timelineOn} onClick={() => setTimelineMode(false)}>This conversation</button>
                <button aria-pressed={timelineOn} onClick={() => setTimelineMode(true)}>All channels<span className="cx-num">{(selItem.otherConversationCount || 0) + 1}</span></button>
              </div>
              {combined && (
                <label className="cx-tl-reply">
                  <span>Reply on</span>
                  <span className="cx-select"><select value={sel} onChange={(e) => select(e.target.value)} aria-label="Reply on which conversation">
                    {timeline.data.conversations.map((c) => (
                      <option key={c.id} value={c.id}>{channelLabel(SRC_CH[c.source] || 'email')}{c.ref ? ` · ${c.ref}` : ''}{c.subject ? ` · ${c.subject.slice(0, 40)}` : ''}</option>
                    ))}
                  </select><Icon name="down" /></span>
                </label>
              )}
            </div>
          )}
          <div className="cx-msgs" ref={msgsRef}>{renderMessages()}</div>
          <div className="cx-dock">
            {!editing[sel] && selItem?.status !== 'resolved' && (
              <InboxDraft
                api={api}
                ticketId={sel}
                refreshKey={`${draftKey}-${thread?.comms?.length || 0}`}
                windowChip={<WindowChip expiresMs={selItem?.replyWindowExpiresMs} strict={selItem?.channel === 'wa'} />}
                toast={toast}
                onSent={refreshAll}
                onHasDraft={(v) => setHasDraft((p) => (p[sel] === v ? p : { ...p, [sel]: v }))}
                onWriteOwn={(m) => {
                  setOwnReply((p) => ({ ...p, [sel]: true }));
                  setMode((p) => ({ ...p, [sel]: m }));
                  setTimeout(() => rootRef.current?.querySelector('#cx-composer')?.focus(), 0);
                }}
                onEdit={(draft, status) => {
                  setEditing((p) => ({ ...p, [sel]: { draft, status } }));
                  setMode((p) => ({ ...p, [sel]: 'reply' }));
                  setCompose((p) => ({ ...p, [sel]: draft.replyBody }));
                  setTimeout(() => rootRef.current?.querySelector('#cx-composer')?.focus(), 0);
                }}
              />
            )}
            {hasDraft[sel] && !editing[sel] && !ownReply[sel] && selItem?.status !== 'resolved' ? null : (
            <div
              className={`cx-composer${curMode === 'note' ? ' note' : ''}${dragOver ? ' drag' : ''}`}
              onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { if (!e.dataTransfer?.files?.length) return; e.preventDefault(); setDragOver(false); const m = replyFiles.add(sel, e.dataTransfer.files); setSendErr(m); }}
            >
              <div className="cx-comp-tabs">
                <button aria-pressed={curMode === 'reply'} onClick={() => setMode((p) => ({ ...p, [sel]: 'reply' }))}>{isDm ? <ChannelBadge channel={selItem?.channel} /> : <Icon name="send" size={15} />}{editing[sel] ? 'Edit AI draft' : 'Reply'}</button>
                <button aria-pressed={curMode === 'note'} onClick={() => setMode((p) => ({ ...p, [sel]: 'note' }))}><Icon name="lock" size={15} />Internal note</button>
                <span className="cx-to">{curMode === 'note' ? 'Only your team sees this' : toLabel}</span>
              </div>
              {showTemplates && curMode === 'reply' && (
                <div className="cx-templates">{TEMPLATES.map((tpl) => (
                  <button key={tpl} onClick={() => { setCompose((p) => ({ ...p, [sel]: `${p[sel] ? `${p[sel]}\n\n` : ''}${tpl}` })); setShowTemplates(false); }}>{tpl}</button>
                ))}</div>
              )}
              <textarea
                id="cx-composer"
                rows={editing[sel] ? 6 : 2}
                value={compose[sel] || ''}
                onChange={(e) => { setCompose((p) => ({ ...p, [sel]: e.target.value })); setSendErr(null); }}
                placeholder={curMode === 'note' ? 'Add a note for the team' : 'Write a reply, or approve the AI draft above'}
                aria-label="Message"
                onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); const m = replyFiles.add(sel, e.clipboardData.files); setSendErr(m); } }}
              />
              <FileChips files={replyFiles.byId[sel]} onRemove={(key) => replyFiles.remove(sel, key)} />
              {sendErr && <div className="cx-err cx-err-box" role="alert"><Icon name="alert" size={15} /><span>{sendErr}</span></div>}
              <div className="cx-comp-foot">
                <button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => setShowTemplates((v) => !v)} aria-label="Insert a template" aria-expanded={showTemplates}><Icon name="template" /></button>
                <button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => fileInputRef.current?.click()} aria-label="Attach a file" title="Attach photo, PDF or video (or paste / drop it here)"><Icon name="attach" /></button>
                <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { const m = replyFiles.add(sel, e.target.files); setSendErr(m); e.target.value = ''; }} />
                {(editing[sel] || (ownReply[sel] && hasDraft[sel])) && <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => { setEditing((p) => ({ ...p, [sel]: null })); setOwnReply((p) => ({ ...p, [sel]: false })); setMode((p) => ({ ...p, [sel]: 'reply' })); setCompose((p) => ({ ...p, [sel]: '' })); }}>Back to AI draft</button>}
                <span className="cx-spacer" />
                {editing[sel] && curMode === 'reply' && (
                  <>
                    <span className="cx-then">then</span>
                    <span className="cx-select cx-select-status">
                      <select value={editing[sel].status} onChange={(e) => { const v = e.target.value; setEditing((p) => ({ ...p, [sel]: { ...p[sel], status: v } })); }} aria-label="Status after sending">
                        {STATUS_AFTER.map(([v, l]) => <option key={v} value={v}>{l}{editing[sel].draft.suggestedStatus === v ? AI_PICK : ''}</option>)}
                      </select>
                      <Icon name="down" />
                    </span>
                  </>
                )}
                <span className="cx-hint cx-hide-sm"><kbd className="cx-kbd">⌘</kbd> <kbd className="cx-kbd">Enter</kbd></span>
                <button className={`cx-btn${curMode === 'note' ? '' : ' cx-btn-primary'}`} onClick={send}>
                  {curMode === 'note' ? 'Add note' : editing[sel] ? 'Send edited reply' : 'Send'}
                </button>
              </div>
            </div>
            )}
          </div>
        </section>
      )}

      {showCtx && (
        <InboxCustomer
          api={api}
          ticketId={selCart ? null : sel}
          cartId={selCart?.id || null}
          header={selCart ? null : panelHead}
          briefKey={briefKey}
          onClose={() => setCtxOpen(false)}
          onOpenCart={perms.carts ? (cartId) => { setView('carts'); setCartTab('open'); setSel(`cart:${cartId}`); setMThread(true); } : null}
          onOpenConversation={(id) => { setView('all'); setSel(id); setMThread(true); setCtxOpen(false); }}
          onLinked={loadItems}
          toast={toast}
        />
      )}

      <ProblemReport api={api} toast={toast} ticketId={sel} channel={selItem?.channel || null} />
      {keysOpen && (
        <div className="cx-keys" onClick={(e) => e.target === e.currentTarget && setKeysOpen(false)}>
          <div className="cx-keys-panel" role="dialog" aria-label="Keyboard shortcuts">
            <div className="cx-keys-h"><h2>Keyboard shortcuts</h2><button className="cx-btn cx-btn-icon cx-btn-ghost" onClick={() => setKeysOpen(false)} aria-label="Close"><Icon name="x" /></button></div>
            <dl>
              <dt>Next or previous</dt><dd><kbd className="cx-kbd">J</kbd><kbd className="cx-kbd">K</kbd></dd>
              <dt>Approve AI draft</dt><dd><kbd className="cx-kbd">A</kbd></dd>
              <dt>Edit AI draft</dt><dd><kbd className="cx-kbd">E</kbd></dd>
              <dt>Resolve</dt><dd><kbd className="cx-kbd">R</kbd></dd>
              <dt>Snooze</dt><dd><kbd className="cx-kbd">S</kbd></dd>
              <dt>Search</dt><dd><kbd className="cx-kbd">/</kbd></dd>
              <dt>Send</dt><dd><kbd className="cx-kbd">⌘</kbd><kbd className="cx-kbd">Enter</kbd></dd>
            </dl>
          </div>
        </div>
      )}
      <div className={`cx-toast${toastMsg ? ' show' : ''}${toastMsg?.isErr ? ' err' : ''}`} role="status" aria-live="polite">
        {toastMsg && !toastMsg.isErr && <Icon name="check" size={16} />}{toastMsg?.msg}
      </div>
    </div>
  );
}

export default Inbox;
